/**
 * Footer — Professional dark footer with links and social
 * Design: Dark navy background, green accents, Inter font
 */

import React from "react";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/pxebeeu2_Legacy%20triangle%20logo.png";

const companyLinks = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "Funding Options", href: "/funding-options" },
  { label: "Business Credit Builder", href: "/business-credit-builder" },
  { label: "Contact Us", href: "/contact" },
];

const resourceLinks = [
  { label: "Blog", href: "/blog" },
  { label: "Resources", href: "/resources" },
  { label: "FAQ", href: "/faq" },
  { label: "Business Credit Builder", href: "/business-credit-builder" },
  { label: "Affiliate Program", href: "/affiliate" },
  { label: "Partners", href: "/partners" },
  { label: "Investing Circle", href: "/invest", highlight: true },
  { label: "Acquire a Business", href: "/business-acquisition", highlight: true },
];

const fundingTypes = [
  { label: "Business Credit Cards", href: "/funding-options" },
  { label: "SBA Loans", href: "/funding-options" },
  { label: "Business Line of Credit", href: "/funding-options" },
  { label: "Equipment Financing", href: "/funding-options" },
  { label: "Working Capital", href: "/funding-options" },
  { label: "Merchant Cash Advance", href: "/funding-options" },
  { label: "Business Credit Builder", href: "/business-credit-builder" },
];

const legalLinks = [
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms of Service", href: "/terms-of-service" },
  { label: "Disclaimer", href: "/disclaimer" },
  { label: "Cookie Policy", href: "/cookie-policy" },
];

export default function Footer() {
  return (
    <footer className="bg-[#091e38] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">

          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-5">
              <img src={LOGO_URL} alt="Legacy Triangle" className="h-20 w-20" />
              <div>
                <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-white font-bold text-lg leading-tight">
                  Legacy Triangle
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-[#48d480] text-xs font-bold tracking-widest uppercase">
                  Start, Fund & Grow
                </div>
              </div>
            </Link>
            <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-sm leading-relaxed mb-6 max-w-xs">
              Connecting businesses with the right funding solutions since 2015. Expert guidance, transparent process, real results.
            </p>

            {/* Contact */}
            <div className="space-y-3 mb-6">
              <a href="tel:+19844644672" className="flex items-center gap-3 text-white/70 hover:text-[#48d480] transition-colors text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                <Phone size={14} className="text-[#48d480]" /> (984) 464-4672
              </a>
              <a href="mailto:info@legacytriangle.com" className="flex items-center gap-3 text-white/70 hover:text-[#48d480] transition-colors text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                <Mail size={14} className="text-[#48d480]" /> info@legacytriangle.com
              </a>
              <div className="flex items-start gap-3 text-white/70 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                <MapPin size={14} className="text-[#48d480] mt-0.5 flex-shrink-0" />
                <span>207 W Millbrook Road, Suite 210<br />Raleigh, NC 27609</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/legacytriangle"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#48d480] transition-colors group"
              >
                <Facebook size={16} className="text-white/70 group-hover:text-[#0f2d52]" />
              </a>
              <a
                href="https://www.instagram.com/legacytriangle"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#48d480] transition-colors group"
              >
                <Instagram size={16} className="text-white/70 group-hover:text-[#0f2d52]" />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 style={{ fontFamily: "'Inter', sans-serif" }} className="text-white font-bold text-sm tracking-widest uppercase mb-5">
              Company
            </h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    className="text-white/60 hover:text-[#48d480] text-sm transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#48d480] opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                    {link.comingSoon && (
                      <span className="text-[8px] bg-[#e8821c] text-white px-1.5 py-0.5 rounded-full font-bold uppercase">
                        Soon
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources & Tools */}
          <div>
            <h4 style={{ fontFamily: "'Inter', sans-serif" }} className="text-white font-bold text-sm tracking-widest uppercase mb-5">
              Resources
            </h4>
            <ul className="space-y-3">
              {resourceLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    className={`text-sm transition-colors flex items-center gap-2 group ${
                      link.highlight 
                        ? 'text-[#e8821c] hover:text-[#f59542] font-semibold' 
                        : 'text-white/60 hover:text-[#48d480]'
                    }`}
                  >
                    <span className={`w-1 h-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity ${
                      link.highlight ? 'bg-[#e8821c]' : 'bg-[#48d480]'
                    }`} />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Funding Options + Newsletter */}
          <div>
            <h4 style={{ fontFamily: "'Inter', sans-serif" }} className="text-white font-bold text-sm tracking-widest uppercase mb-5">
              Funding Options
            </h4>
            <ul className="space-y-3 mb-8">
              {fundingTypes.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    className="text-white/60 hover:text-[#48d480] text-sm transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#48d480] opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Newsletter */}
            <h4 style={{ fontFamily: "'Inter', sans-serif" }} className="text-white font-bold text-sm tracking-widest uppercase mb-3">
              Stay Informed
            </h4>
            <form onSubmit={(e) => e.preventDefault()} className="space-y-2">
              <input
                type="email"
                placeholder="Enter your email"
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#48d480] transition-colors"
              />
              <button
                type="submit"
                className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-white text-sm font-bold py-2.5 rounded-lg transition-colors"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/40 text-xs">
            © 2026 Legacy Triangle. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            {legalLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="text-white/40 hover:text-white/70 text-xs transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
          <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/30 text-xs text-center">
            Funding is not guaranteed. Terms apply.
          </p>
        </div>
      </div>
    </footer>
  );
}
